import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { getToken } from '@/services/http';
import AppLayout from '@/components/layout/AppLayout';

// 懒加载页面
const SplashPage = lazy(() => import('@/pages/SplashPage'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage'));
const MapPage = lazy(() => import('@/pages/MapPage'));
const ChatPage = lazy(() => import('@/pages/ChatPage'));
const SubscriptionPage = lazy(() => import('@/pages/SubscriptionPage'));
const TimelinePage = lazy(() => import('@/pages/TimelinePage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const AccentPage = lazy(() => import('@/pages/AccentPage'));
const AccentPracticePage = lazy(() => import('@/pages/AccentPracticePage'));
const NotFound = lazy(() => import('@/pages/NotFound'));

// 加载占位组件
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="w-8 h-8 border-4 border-warm-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

// 路由守卫
function AuthGuard({ children }: { children: React.ReactNode }) {
  const token = getToken();
  const mockAuth = import.meta.env.VITE_MOCK_AUTH === 'true';
  if (!token && !mockAuth) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <LazyPage>
        <SplashPage />
      </LazyPage>
    ),
  },
  {
    path: '/auth/callback',
    element: (
      <LazyPage>
        <AuthCallback />
      </LazyPage>
    ),
  },
  {
    path: '/onboarding',
    element: (
      <LazyPage>
        <AuthGuard>
          <OnboardingPage />
        </AuthGuard>
      </LazyPage>
    ),
  },
  {
    path: '/',
    element: <AppLayout />,
    children: [
      {
        path: 'map',
        element: (
          <LazyPage>
            <AuthGuard>
              <MapPage />
            </AuthGuard>
          </LazyPage>
        ),
      },
      {
        path: 'chat/:sceneId',
        element: (
          <LazyPage>
            <AuthGuard>
              <ChatPage />
            </AuthGuard>
          </LazyPage>
        ),
      },
      {
        path: 'subscription',
        element: (
          <LazyPage>
            <AuthGuard>
              <SubscriptionPage />
            </AuthGuard>
          </LazyPage>
        ),
      },
      {
        path: 'timeline',
        element: (
          <LazyPage>
            <AuthGuard>
              <TimelinePage />
            </AuthGuard>
          </LazyPage>
        ),
      },
      {
        path: 'settings',
        element: (
          <LazyPage>
            <AuthGuard>
              <SettingsPage />
            </AuthGuard>
          </LazyPage>
        ),
      },
      {
        path: 'accent',
        element: (
          <LazyPage>
            <AuthGuard>
              <AccentPage />
            </AuthGuard>
          </LazyPage>
        ),
      },
      {
        path: 'accent/practice/:mode',
        element: (
          <LazyPage>
            <AuthGuard>
              <AccentPracticePage />
            </AuthGuard>
          </LazyPage>
        ),
      },
    ],
  },
  {
    path: '*',
    element: (
      <LazyPage>
        <NotFound />
      </LazyPage>
    ),
  },
]);